import { useState, useMemo, useEffect } from 'react';
import { Dropdown, Menu } from 'antd';
import { NavItemConfig } from '../Siderbar.constant';
import DebounceInput from '../../DebouncedInput';
import NavItem from './NavItem';
import { useIsMobile } from 'Hooks';
import Icon from 'Components/Icon/Icon';
import Button from 'Components/Button/Button';
import { useTranslation } from 'react-i18next';

type Props = {
    items: NavItemConfig[],
    loading: boolean,
    isTenantSelector: boolean,
    disableDropDown: boolean,
    selectedItem: { label: string, value: number },
    handleItemClick: (val: NavItemConfig) => void;
}

const SearchDropDown = ({ items, loading, isTenantSelector, disableDropDown, selectedItem, handleItemClick }: Props) => {
    const [search, setSearch] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<NavItemConfig | null>(null);
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const tenantMobileView = isTenantSelector && isMobile;

    const handleItemSelection = (item: NavItemConfig, isMobileView: boolean) => {
        if(isTenantSelector && isMobileView) {
            setSelectedValue(item);
        } else {
            setIsDropdownOpen(false);
            handleItemClick(item);
        }
    }

    const filteredList: NavItemConfig[] = useMemo(() => {
        return search ? items.filter(tenant => tenant.label.toLowerCase().includes(search.toLowerCase())) : items;
    }, [items, search])

    useEffect(() => {
        if (!isDropdownOpen) {
            if(search) setSearch("");
            if(selectedValue) setSelectedValue(null);
        }
    }, [isDropdownOpen])

    return <Dropdown
        disabled={disableDropDown}
        trigger={["click"]}
        placement="bottomLeft"
        visible={isDropdownOpen}
        align={{ offset: [5, 5] }}
        overlayClassName={tenantMobileView ? "mobile-bottom-dropdown" : ""}
        getPopupContainer={() => document.body}
        onVisibleChange={setIsDropdownOpen}
        overlay={
            <div className={`custom-dropdown tenant-dropdown`}>
                {tenantMobileView && <div className='header'>
                    <span>{t("sidebar.tenantSelector.tenantList")}</span>
                    <Icon type='close' onClick={() => setIsDropdownOpen(false)} />
                </div>}
                <div className='search-bar'>
                    <DebounceInput debounceSearch={search} placeholder={t(isTenantSelector ? "sidebar.tenantSelector.searchTenant" : "pciCompliance.searchPlaceholder")} setDebouncedSearch={setSearch} />
                </div>
                {(loading || !filteredList.length) ? <div className='empty-data'>{loading ? t('phishingManager.campaignSetup.step5.loading') : t(isTenantSelector ? "sidebar.tenantSelector.noTenantsFound" : "phishingManager.campaignSetup.step3.property.noPropertiesFound")}</div> : <>
                <Menu>
                    {filteredList.map((item) => (
                        <NavItem
                            key={item.key}
                            item={item}
                            isActive={selectedValue === null ? `${selectedItem.value}` === item.path : selectedValue.path === item.path}
                            isOpen={false}
                            hasChildren={false}
                            onClick={() => handleItemSelection(item, isMobile)}
                        />
                    ))}
                </Menu>
                {tenantMobileView && <div className='footer'>
                    <Button shape="round" type="primary" className={selectedValue === null ? 'cta-disabled': ''} disabled={selectedValue === null} onClick={() => {if(selectedValue) handleItemSelection(selectedValue, false)}}>
                        {t("select")}
                    </Button>
                    </div>}
                </>
                }
            </div>
        }>
        <div className={`selected-item ${selectedItem.value == 0 ? "no-selection" : ""}`}>
            <NavItem {...{ item: { key: selectedItem.label, label: selectedItem.label, path: `${selectedItem.value}`, icon: <Icon type="recipientProperty" /> }, isActive: selectedItem.value != 0, isOpen: false, hasChildren: true, onClick: () => null }} />
            {(isTenantSelector && !isMobile) && <div className='divider'></div>}
        </div>
    </Dropdown>
}

export default SearchDropDown;